/*
  Flourishing Fragrances — permanent variant-specific URL controller (v5)

  This file is the single source of truth for product ?variant= URLs.

  Goals:
  - Exact complete picker selection -> ?variant=VARIANT_ID
  - Incoming ?variant=VARIANT_ID stays authoritative on reload/share/new tab
  - No complete selection -> no stale variant parameter
  - Shopify async section updates may mirror the chosen variant, but may never
    overwrite a newer user choice or turn an incomplete choice into a fallback
  - No timers that restore or re-apply a default variant
*/
(function () {
  'use strict';

  if (window.__ffPermanentVariantUrlSyncV5) return;
  window.__ffPermanentVariantUrlSyncV5 = true;

  const dataNode = document.getElementById('FFVariantUrlData');
  if (!dataNode) return;

  let data;
  try {
    data = JSON.parse(dataNode.textContent || '{}');
  } catch (error) {
    return;
  }

  const variants = Array.isArray(data.variants) ? data.variants : [];
  const isGiftCard = data.giftCard === true;
  if (!variants.length) return;

  let desiredVariantId = '';
  let selectionState = 'idle'; // idle | exact | incomplete | url
  let pubSubBound = false;
  // History state is specific to this one page visit. Unlike localStorage it
  // does not reselect an old scent on a NEW visit to a clean product URL.
  const partialKey = '__ffPartialProductOptions';
  const productId = String(data.productId || '');

  function partialFromHistory() {
    const state = window.history?.state;
    const entry = state && typeof state === 'object' ? state[partialKey] : null;
    if (!entry || String(entry.productId) !== productId || !Array.isArray(entry.options)) return null;
    return entry.options.map(v => String(v || ''));
  }

  function nextHistoryState(variantId) {
    const old = window.history?.state;
    const state = old && typeof old === 'object' && !Array.isArray(old) ? { ...old } : {};
    delete state[partialKey];
    if (!variantId) {
      const values = getSelectedOptionValues();
      if (values.length && values.some(Boolean)) {
        state[partialKey] = { productId, options: values };
      }
    }
    return state;
  }

  function getMainProduct() {
    return (
      document.querySelector('product-info[id^="MainProduct-"]') ||
      document.querySelector('product-info[data-product-id]') ||
      document.querySelector('[id^="MainProduct-"]')
    );
  }

  function getPicker() {
    const root = getMainProduct();
    return (
      root?.querySelector('variant-selects[id^="variant-selects-"]') ||
      root?.querySelector('variant-selects') ||
      root?.querySelector('variant-radios') ||
      document.querySelector('variant-selects[id^="variant-selects-"]') ||
      document.querySelector('variant-radios')
    );
  }

  function getVariantById(id) {
    if (id == null || id === '') return null;
    return variants.find(function (variant) {
      return String(variant.id) === String(id);
    }) || null;
  }

  function getSelectedOptionValues() {
    const picker = getPicker();
    if (!picker) return [];

    const selects = Array.from(picker.querySelectorAll('select[name^="options["]'));
    if (selects.length) {
      return selects.map(function (select) {
        return select.value || '';
      });
    }

    const fieldsets = Array.from(picker.querySelectorAll('fieldset'));
    if (!fieldsets.length) return [];

    return fieldsets.map(function (fieldset) {
      const checked = fieldset.querySelector(
        'input[type="radio"][name^="options["]:checked'
      );
      return checked ? checked.value : '';
    });
  }

  function getExactVariantFromPicker() {
    const values = getSelectedOptionValues();
    const optionCount = Array.isArray(variants[0]?.options)
      ? variants[0].options.length
      : 0;

    if (
      !optionCount ||
      values.length !== optionCount ||
      values.some(function (value) { return !value; })
    ) {
      return null;
    }

    return variants.find(function (variant) {
      return Array.isArray(variant.options) && variant.options.every(function (optionValue, index) {
        return String(optionValue) === String(values[index]);
      });
    }) || null;
  }

  function buildCurrentUrlWithVariant(variantId) {
    const url = new URL(window.location.href);

    if (isGiftCard || !variantId) {
      url.searchParams.delete('variant');
    } else {
      url.searchParams.set('variant', String(variantId));
    }

    return url;
  }

  function commitUrl(variantId) {
    if (!window.history || !window.history.replaceState) return false;

    const variant = variantId ? getVariantById(variantId) : null;
    if (variantId && !variant) return false;

    try {
      const url = buildCurrentUrlWithVariant(variant ? variant.id : '');
      const nextUrl =
        url.pathname +
        (url.searchParams.toString() ? '?' + url.searchParams.toString() : '') +
        url.hash;

      const currentUrl =
        window.location.pathname +
        window.location.search +
        window.location.hash;

      const state = nextHistoryState(variant ? String(variant.id) : '');
      const oldState = window.history.state;
      const oldPartial = oldState && typeof oldState === 'object' ? oldState[partialKey] : null;
      const newPartial = state[partialKey] || null;
      if (nextUrl !== currentUrl || JSON.stringify(oldPartial) !== JSON.stringify(newPartial)) {
        window.history.replaceState(state, '', nextUrl);
      }

      const root = getMainProduct();
      const shareButton = root?.querySelector('share-button');
      if (shareButton && typeof shareButton.updateUrl === 'function') {
        shareButton.updateUrl(url.toString());
      }

      document.dispatchEvent(new CustomEvent('ff:variant-url-updated', {
        detail: {
          variantId: variant ? String(variant.id) : '',
          variant: variant || null
        }
      }));

      return true;
    } catch (error) {
      return false;
    }
  }

  function commitFromPickerNow() {
    const exactVariant = getExactVariantFromPicker();

    if (exactVariant) {
      desiredVariantId = String(exactVariant.id);
      selectionState = 'exact';
      commitUrl(desiredVariantId);
      return exactVariant;
    }

    /* A partially completed picker must never keep an old exact variant URL. */
    desiredVariantId = '';
    selectionState = 'incomplete';
    commitUrl('');
    return null;
  }

  function isMainPickerControl(target) {
    const picker = getPicker();
    if (!picker || !target || !picker.contains(target)) return false;

    return target.matches(
      'select[name^="options["], input[type="radio"][name^="options["]'
    );
  }

  /* Capture phase is intentional: the URL is updated before Shopify starts its
     async section request for this selection. */
  function handlePickerInputImmediately(event) {
    if (!isMainPickerControl(event.target)) return;
    commitFromPickerNow();
  }

  document.addEventListener('input', handlePickerInputImmediately, true);
  document.addEventListener('change', handlePickerInputImmediately, true);

  function shouldAcceptShopifyVariant(variantId) {
    const id = variantId == null ? '' : String(variantId);

    if (!id) return true;
    // Shopify may publish an initial first-available variant without a click.
    // A clean URL must remain unselected, even if the server prefilled an ID.
    if (selectionState === 'idle' || selectionState === 'incomplete') return false;
    if (desiredVariantId && id !== desiredVariantId) return false;

    return true;
  }

  function syncFromShopifyVariant(variantId) {
    const variant = getVariantById(variantId);
    if (!variant) return false;
    if (!shouldAcceptShopifyVariant(variant.id)) return false;

    const exactPickerVariant = getExactVariantFromPicker();

    /* If the live picker is complete, Shopify may only mirror that same exact
       variant. This blocks a late/stale response from rewriting the URL. */
    if (
      exactPickerVariant &&
      String(exactPickerVariant.id) !== String(variant.id)
    ) {
      return false;
    }

    desiredVariantId = String(variant.id);
    if (selectionState !== 'url') selectionState = 'exact';
    return commitUrl(desiredVariantId);
  }

  function bindShopifyPubSub() {
    if (pubSubBound) return;
    if (typeof subscribe !== 'function') return;
    if (typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.variantChange) return;

    pubSubBound = true;
    subscribe(PUB_SUB_EVENTS.variantChange, function (payload) {
      const eventData = payload && payload.data ? payload.data : payload;
      const eventVariant = eventData && eventData.variant;
      if (!eventVariant || eventVariant.id == null) return;

      syncFromShopifyVariant(eventVariant.id);
    });
  }

  function restorePartialFromHistory() {
    const values = partialFromHistory();
    const picker = getPicker();
    if (!values || !picker || new URL(window.location.href).searchParams.has('variant')) return;
    const selects = Array.from(picker.querySelectorAll('select[name^="options["]'));
    if (!selects.length || values.length !== selects.length) return;
    const changed = [];
    for (let i = 0; i < selects.length; i++) {
      const select = selects[i];
      const value = values[i];
      if (!Array.from(select.options).some(option => option.value === value)) return;
      if (select.value !== value) {
        select.value = value;
        changed.push(select);
      }
      Array.from(select.options).forEach(option => {
        if (option.value === value) option.setAttribute('selected', 'selected');
        else option.removeAttribute('selected');
      });
    }
    // Native change events update the existing custom Scent/Finish widgets,
    // Scent Details, buy-button state and Shopify's section-rendered options.
    changed.forEach(select => select.dispatchEvent(new Event('change', { bubbles: true })));
    if (!changed.length) {
      // Safari may restore the native selects itself; do not treat those values
      // as a new complete choice, but preserve the partial state.
      selectionState = 'incomplete';
    }
  }

  function initialize() {
    try {
      const incomingId = new URL(window.location.href).searchParams.get('variant') || '';
      const incomingVariant = getVariantById(incomingId);

      if (incomingVariant) {
        desiredVariantId = String(incomingVariant.id);
        selectionState = 'url';
      } else {
        desiredVariantId = '';
        selectionState = 'idle';
      }
    } catch (error) {
      desiredVariantId = '';
      selectionState = 'idle';
    }

    bindShopifyPubSub();
    setTimeout(bindShopifyPubSub, 100);
    setTimeout(bindShopifyPubSub, 400);
    if (selectionState === 'idle') restorePartialFromHistory();
  }

  /* Public bridge used by product-info.js. product-info.js no longer writes
     history directly when this controller exists. */
  window.FFVariantURLSync = {
    getExactVariantId: function () {
      const variant = getExactVariantFromPicker();
      return variant ? String(variant.id) : '';
    },
    getDesiredVariantId: function () {
      return desiredVariantId;
    },
    getSelectionState: function () {
      return selectionState;
    },
    shouldAcceptShopifyVariant: shouldAcceptShopifyVariant,
    syncFromShopifyVariant: syncFromShopifyVariant,
    clearVariant: function () {
      desiredVariantId = '';
      selectionState = 'incomplete';
      return commitUrl('');
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
